import { useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { Download } from "lucide-react";
import { t, useLanguage } from "@/i18n/LanguageContext";
import { COMPANY_BROCHURE } from "@/content/brochure";
import { ExcavatorMarquee } from "@/components/ExcavatorMarquee";
import "@/components/brochure-download.css";

export const BrochureDownload = ({ placement }) => {
  const { locale } = useLanguage();
  const prefix = `brochure-${placement}`;
  const size = (COMPANY_BROCHURE.bytes / 1000000).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef, { amount: 0.1, margin: "100px" });
  const reducedMotion = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const playing = visible && !paused && !hovered && !reducedMotion;
  const motionState = reducedMotion ? "reduced" : paused ? "paused" : hovered ? "hover-paused" : visible ? "running" : "offscreen";
  const handlePointerOver = (event) => {
    if (event.pointerType === "mouse") setHovered(!event.target.closest("[data-brochure-motion-control]"));
  };
  return (
    <section ref={sectionRef} data-testid={`${prefix}-section`} aria-labelledby={`${prefix}-title`} className="gfc-brochure gfc-brochure--construction" data-motion={motionState} style={{ "--brochure-play-state": playing ? "running" : "paused" }} onPointerOver={handlePointerOver} onPointerLeave={() => setHovered(false)}>
      <div data-testid={`${prefix}-stripes`} className="gfc-brochure-stripes" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 sm:py-12 md:grid-cols-[minmax(0,1fr)_minmax(220px,300px)] md:gap-12 lg:gap-20 lg:px-8">
        <div className="gfc-brochure-copy min-w-0">
          <p data-testid={`${prefix}-eyebrow`} className="gfc-brochure-eyebrow mb-4 border-l-2 pl-3 font-mono text-xs font-semibold">{t("Profilo aziendale")}</p>
          <h2 id={`${prefix}-title`} data-testid={`${prefix}-title`} className="font-display text-base font-bold leading-relaxed md:text-lg">{t("Scarica la brochure aziendale 2026–2027")}</h2>
          <p data-testid={`${prefix}-description`} className="gfc-brochure-description mt-3 max-w-2xl text-sm leading-7 md:text-base">{t("La nostra identità, le competenze e i progetti: GFC Appalti Generali in un unico documento.")}</p>
          <p id={`${prefix}-metadata`} data-testid={`${prefix}-metadata`} className="gfc-brochure-metadata mt-5 font-mono text-xs leading-6">{t(`PDF in italiano · ${COMPANY_BROCHURE.pages} pagine · ${size} MB`)}</p>
        </div>
        <a
          data-testid={`${prefix}-download`}
          href={COMPANY_BROCHURE.url}
          download={COMPANY_BROCHURE.filename}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("Scarica la brochure aziendale 2026–2027")}
          aria-describedby={`${prefix}-metadata`}
          className="gfc-brochure-logo-link flex w-full max-w-[300px] flex-col items-center justify-center gap-4 justify-self-start rounded-lg border bg-white px-6 py-5 md:justify-self-end">
          <img data-testid={`${prefix}-logo`} src={COMPANY_BROCHURE.logo} alt={t("Logo GFC Appalti Generali")} width="1810" height="869" loading="lazy" decoding="async" className="block h-auto w-full max-w-[240px] object-contain" />
          <span data-testid={`${prefix}-download-label`} className="flex max-w-full items-center gap-2 text-center font-display text-xs font-semibold text-brand-navy"><Download aria-hidden="true" className="h-4 w-4 shrink-0" />{t("Scarica PDF")}</span>
        </a>
      </div>
      <ExcavatorMarquee prefix={prefix} paused={paused} reducedMotion={reducedMotion} motionState={motionState} onToggle={() => setPaused((value) => !value)} />
    </section>
  );
};